import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTimes, faWheelchair, faUserPlus } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { Paciente } from '../types';
import { PacienteService } from '../services/pacienteService';
import Modal from './Modal';
import Input from './Input';
import Checkbox from './Checkbox';
import Button from './Button';

interface AddToQueueModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (pacienteId: string, isPriority: boolean) => void;
  loading?: boolean;
  organizationId: string;
}

const AddToQueueModal: React.FC<AddToQueueModalProps> = ({
  isOpen,
  onClose,
  onAdd,
  loading = false,
  organizationId
}) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [pacientes, setPacientes] = useState<Paciente[]>([]);
  const [selectedPaciente, setSelectedPaciente] = useState<Paciente | null>(null);
  const [isPriority, setIsPriority] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);

  const searchPacientes = async (term: string) => {
    if (!term.trim() || !organizationId) {
      setPacientes([]);
      return;
    }

    setSearchLoading(true);
    try {
      const response = await PacienteService.searchPacientes(organizationId, term, 10);
      if (response.success && response.data) {
        setPacientes(response.data);
      } else {
        console.error('Error searching patients:', response.error);
        setPacientes([]);
      }
    } finally {
      setSearchLoading(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchPacientes(searchTerm);
    }, 300);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, organizationId]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPaciente) {
      return;
    }

    onAdd(selectedPaciente.id, isPriority);
    handleClose();
  };

  const handleClose = () => {
    setPacientes([]);
    setSelectedPaciente(null);
    setSearchTerm('');
    setIsPriority(false);
    onClose();
  };

  const handleSelectPaciente = (paciente: Paciente) => {
    setSelectedPaciente(paciente);
    setSearchTerm(paciente.nome || '');
    setPacientes([]);
  };

  const handleCreatePatient = () => {
    const queryParams = new URLSearchParams({
      fromQueue: 'true',
      isPriority: isPriority.toString(),
      searchTerm: searchTerm.trim()
    });
    onClose();
    navigate(`/pacientes/create?${queryParams.toString()}`);
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Adicionar Paciente à Fila">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="relative">
          <Input
            label="Buscar Paciente"
            type="text"
            value={searchTerm}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
            placeholder="Digite o nome ou CPF do paciente..."
            leftIcon={faSearch}
            rightIcon={searchLoading ? undefined : searchTerm ? faTimes : undefined}
            onRightIconClick={searchTerm ? () => setSearchTerm('') : undefined}
            required
          />

          {pacientes.length > 0 && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
              {pacientes.map(paciente => (
                <button
                  key={paciente.id}
                  type="button"
                  className="w-full px-4 py-2 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none"
                  onClick={() => handleSelectPaciente(paciente)}
                >
                  <div className="font-medium text-gray-900">{paciente.nome}</div>
                  {paciente.cpf && <div className="text-sm text-gray-500">CPF: {paciente.cpf}</div>}
                </button>
              ))}
            </div>
          )}
        </div>

        {!selectedPaciente && searchTerm && pacientes.length === 0 && !searchLoading && (
          <div className="p-4 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
            <div className="text-sm text-gray-600 mb-3">
              Nenhum paciente encontrado. Verifique o nome ou CPF digitado.
            </div>
            <Button
              type="button"
              variant="primary"
              leftIcon={faUserPlus}
              onClick={handleCreatePatient}
              className="w-full"
            >
              Cadastrar Novo Paciente
            </Button>
          </div>
        )}

        <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 cursor-pointer">
          <span>
            <Checkbox
              checked={isPriority}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setIsPriority(e.target.checked)}
            />
          </span>
          <FontAwesomeIcon icon={faWheelchair} className="w-4 h-4 text-blue-600" />
          <span>Atendimento prioritário</span>
        </label>

        <div className="flex justify-end space-x-3 pt-4">
          <Button type="button" variant="secondary" onClick={handleClose} disabled={loading}>
            Cancelar
          </Button>
          <Button type="submit" variant="primary" loading={loading} disabled={!selectedPaciente}>
            Adicionar à Fila
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default AddToQueueModal;
